/** @jsxImportSource @emotion/react */
import React from 'react'
import { Button } from 'react-bootstrap'
import { ChargeAccountLineApplication } from 'store/types'
import { NotesIndicator } from 'components/lab/billingNotes/BillingNotes'
import { serviceLineGrid } from './ServiceLineRow'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from '../user/permissions'
import { formatEasternDate } from 'helpers/dates'

interface Props {
  application: ChargeAccountLineApplication
  onOpenNotes: () => void
  onReturnToUnapplied: () => void
  // A simple receipt for the patient payment this credit came from.
  onReceipt?: (prepaymentId: string) => void
}

const formatDate = (date: string | Date | null | undefined) => formatEasternDate(date, '--')

const formatMoney = (value: number | undefined | null) => {
  return `$${Number(value || 0).toFixed(2)}`
}

// The patient's own money on a procedure line, sitting under the payer
// transactions that stated what they owe. The credit belongs to the charge, so
// a credit covering several lines shows on each of them and offers its return
// action only on the first.
export const PatientApplicationRow: React.FC<Props> = ({
  application,
  onOpenNotes,
  onReturnToUnapplied,
  onReceipt,
}) => {
  const canPostPatientPayments = useLabPermission(UserPermission.POST_PATIENT_PAYMENTS)
  const isPrepayment = application.sourceType === 'PREPAYMENT'
  // A statement payment comes back as credit of its own, so it returns too.
  const isPatientMoney = isPrepayment || application.sourceType === 'STATEMENT'
  const spansOtherLines = Number(application.applicationAmount) !== Number(application.appliedAmount)
  const canReturn = canPostPatientPayments
    && isPatientMoney
    && application.isPrimarySlice
    && Number(application.applicationAmount || 0) > 0

  return (
    <div
      css={{
        ...serviceLineGrid,
        padding: '0.6rem 0',
        backgroundColor: '#F3F8FF',
        borderBottom: '1px solid #cddff5',
      }}>
      <div />
      {/* Info block sits under CPT…Exp. Allowed, the same span the payer rows
          use, so the money columns keep lining up. */}
      <div css={{ gridColumn: 'span 5', fontSize: '0.875rem' }}>
        <div>
          {formatDate(application.effectiveDate)}{' '}
          <span css={{ fontWeight: 500 }}>{application.sourceLabel}</span>
          <span
            css={{
              marginLeft: '0.5rem',
              backgroundColor: '#0d6efd',
              color: 'white',
              borderRadius: '0.25rem',
              padding: '0.05rem 0.35rem',
              fontSize: '0.688rem',
              fontWeight: 600,
              textTransform: 'uppercase',
            }}>
            Patient
          </span>
        </div>
        <div css={{ color: '#6c757d' }}>
          {application.appliedByName ? `Applied by ${application.appliedByName}` : 'Applied automatically'}
        </div>
        {application.note && (
          <div css={{ color: '#6c757d', fontSize: 12 }}>{application.note}</div>
        )}
      </div>
      {/* Allowed, Paid/Refund, Transfer belong to the payer's adjudication. */}
      <div />
      <div />
      <div />
      {/* Under Patient Resp.: what this line's share of the credit settles. */}
      <div css={{ color: '#0a58ca', fontWeight: 600 }}>
        {`−${formatMoney(application.appliedAmount)}`}
      </div>
      <div />
      <div
        className="d-flex justify-content-end align-items-center"
        css={{ gridColumn: 'span 3', flexWrap: 'nowrap', gap: '0.5rem', paddingRight: '0.5rem' }}>
        {spansOtherLines && !application.isPrimarySlice && (
          <span css={{ color: '#6c757d', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
            part of {formatMoney(application.applicationAmount)}
          </span>
        )}
        <NotesIndicator
          count={(application.notes ?? []).length}
          isOpen={false}
          onToggle={onOpenNotes}
        />
        {/* One receipt per payment, so a credit spread over several lines
            offers it where it offers its return: on the first line. */}
        {onReceipt && isPrepayment && application.isPrimarySlice && application.sourcePrepaymentId && (
          <Button
            variant="outline-secondary"
            size="sm"
            title="Print a simple receipt for this patient payment"
            css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}
            onClick={(event) => {
              event.stopPropagation()
              onReceipt(application.sourcePrepaymentId!)
            }}>
            Receipt
          </Button>
        )}
        {canReturn && (
          <Button
            variant="outline-danger"
            size="sm"
            css={{ whiteSpace: 'nowrap', padding: '0.25rem 0.5rem', fontSize: '0.813rem' }}
            onClick={(event) => {
              event.stopPropagation()
              onReturnToUnapplied()
            }}>
            {/* The amount is named only when returning takes back more than
                the row shows — on a credit this line holds in full, the row's
                own amount already says it. */}
            {spansOtherLines
              ? `Return ${formatMoney(application.applicationAmount)} to Unapplied`
              : 'Return to Unapplied'}
          </Button>
        )}
      </div>
    </div>
  )
}
